import React from 'react';
import { Link } from 'react-router-dom';
import CompanyLogo from '../../../components/Common/CompanyLogo';
import { CompanyResultRow } from '../useCompanyResults';
import { useResultsView } from '../useResultsView';
import {
  aggregateVariation, BASE_NON_POSITIVE_HINT, formatPct, Metric, METRICS, metricValue, NEGATIVE,
  PeriodKey, POSITIVE, rowVariation, signed, SortKey, toMillions,
} from '../resultsModel';

interface CompanyResultsTableProps {
  rows: CompanyResultRow[];
  targetExercice: number;
  baseExercice: number;
}

// Charte (cf. MobileCapital.css) : filtres en teinte bleue, sélection (tri) en primaire violet.
const FILTER = '#3b82f6';
const PRIMARY = '#667eea';

/** Filtre en pastille : surligné quand il est actif (case cochée ou champ renseigné). */
const filterPillStyle = (active: boolean): React.CSSProperties => ({
  fontSize: 13,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
  padding: '6px 12px',
  borderRadius: 999,
  border: `1px solid ${active ? FILTER : 'var(--border-color)'}`,
  background: active ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
  color: active ? FILTER : 'var(--text-muted)',
  fontWeight: active ? 600 : 400,
  transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
});

/** Bouton de tri neutre : garde l'apparence d'un en-tête, gagne le clavier et le lecteur d'écran. */
const sortButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
};

export const CompanyResultsTable: React.FC<CompanyResultsTableProps> = ({ rows, targetExercice, baseExercice }) => {
  const {
    searchTerm, setSearchTerm, publishedOnly, setPublishedOnly, groupBySector, setGroupBySector,
    sort, toggleSort, publishedCount, visibleRows, groups,
  } = useResultsView(rows);

  const sortIcon = (key: SortKey) =>
    sort.key === key ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : <span aria-hidden='true' style={{ opacity: 0.3 }}> ↕</span>;

  /** Colonne triée : en couleur d'accent, comme un filtre actif. */
  const sortColor = (key: SortKey) => (sort.key === key ? PRIMARY : 'var(--text-muted)');

  const ariaSort = (key: SortKey): React.AriaAttributes['aria-sort'] =>
    sort.key === key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';

  const renderValue = (row: CompanyResultRow, metric: Metric, period: PeriodKey) => {
    const value = metricValue(row, metric, period);
    if (value == null) return <span style={{ color: 'var(--text-muted)' }} title='Non publié'>—</span>;
    return <span>{toMillions(value)}</span>;
  };

  const renderVariation = (row: CompanyResultRow, metric: Metric) => {
    const v = rowVariation(row, metric);
    if (v.kind === 'pct') {
      return <span style={{ color: v.value >= 0 ? POSITIVE : NEGATIVE, fontWeight: 600 }}>{formatPct(v.value)}</span>;
    }
    if (v.kind === 'abs') {
      // Base négative ou nulle : un % serait trompeur, on donne l'écart en valeur.
      return (
        <span style={{ color: v.value >= 0 ? POSITIVE : NEGATIVE, fontWeight: 600, cursor: 'help' }} title={BASE_NON_POSITIVE_HINT}>
          {signed(toMillions(v.value), v.value)} M
        </span>
      );
    }
    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  };

  const cellStyle: React.CSSProperties = { padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' };
  const firstColStyle: React.CSSProperties = {
    padding: '8px 10px',
    textAlign: 'left',
    position: 'sticky',
    left: 0,
    background: 'var(--bg-box)',
    zIndex: 1,
    minWidth: 180,
  };
  const groupBorder = '2px solid var(--border-color)';

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          type='text'
          aria-label='Filtrer par société ou symbole'
          placeholder='Filtrer par société ou symbole...'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            flex: '1 1 220px',
            padding: '9px 14px',
            borderRadius: 8,
            border: `1px solid ${searchTerm ? FILTER : 'var(--border-color)'}`,
            background: searchTerm ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-box)',
            color: 'var(--text-heading)',
            fontSize: 14,
          }}
        />
        <label style={filterPillStyle(publishedOnly)}>
          <input type='checkbox' checked={publishedOnly} style={{ accentColor: FILTER }} onChange={e => setPublishedOnly(e.target.checked)} />
          Seulement les publiées ({publishedCount}/{rows.length})
        </label>
        <label style={filterPillStyle(groupBySector)}>
          <input type='checkbox' checked={groupBySector} style={{ accentColor: FILTER }} onChange={e => setGroupBySector(e.target.checked)} />
          Regrouper par secteur
        </label>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className='data-table' style={{ width: '100%', minWidth: 1000, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th rowSpan={2} aria-sort={ariaSort('symbole')} style={{ ...firstColStyle, color: sortColor('symbole'), zIndex: 2 }}>
                <button type='button' style={sortButtonStyle} onClick={() => toggleSort('symbole')}>
                  Société{sortIcon('symbole')}
                </button>
              </th>
              {METRICS.map(m => (
                <th key={m.key} colSpan={3} title={m.title} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '8px 10px', borderLeft: groupBorder }}>
                  {m.label}
                </th>
              ))}
            </tr>
            <tr>
              {METRICS.map(m => (
                <React.Fragment key={m.key}>
                  <th aria-sort={ariaSort(m.key)} style={{ ...cellStyle, color: sortColor(m.key), borderLeft: groupBorder }}>
                    <button type='button' style={sortButtonStyle} onClick={() => toggleSort(m.key)} aria-label={`Trier par ${m.label} ${targetExercice}`}>
                      {targetExercice}{sortIcon(m.key)}
                    </button>
                  </th>
                  <th style={{ ...cellStyle, color: 'var(--text-muted)' }}>{baseExercice}</th>
                  <th aria-sort={ariaSort(`${m.key}_var`)} style={{ ...cellStyle, color: sortColor(`${m.key}_var`) }}>
                    <button type='button' style={sortButtonStyle} onClick={() => toggleSort(`${m.key}_var`)} aria-label={`Trier par variation ${m.label}`}>
                      Var{sortIcon(`${m.key}_var`)}
                    </button>
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(group => (
              <React.Fragment key={group.name ?? 'all'}>
                {group.name && (
                  <tr>
                    <td style={{ ...firstColStyle, background: 'var(--bg-input)', fontWeight: 700, color: 'var(--text-heading)' }}>
                      {group.name}
                      <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted)', marginLeft: 6 }}>
                        ({group.rows.length})
                      </span>
                    </td>
                    {METRICS.map(m => {
                      const agg = aggregateVariation(group.rows, m.key);
                      return (
                        <td
                          key={m.key}
                          colSpan={3}
                          style={{ ...cellStyle, background: 'var(--bg-input)', borderLeft: groupBorder, fontSize: 13 }}
                          title='Variation agrégée du secteur, sur les sociétés comparables des deux exercices'
                        >
                          {agg != null && (
                            <span style={{ color: agg >= 0 ? POSITIVE : NEGATIVE, fontWeight: 600 }}>
                              Secteur {formatPct(agg)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {group.rows.map(row => (
                  <tr key={row.symbole}>
                    <td style={firstColStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <CompanyLogo symbol={row.symbole} size={28} />
                        <div style={{ minWidth: 0 }}>
                          <Link to={`/data?symbol=${row.symbole}`} style={{ fontWeight: 700, color: 'var(--text-heading)', textDecoration: 'none' }}>
                            {row.symbole}
                          </Link>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.nom_societe}
                          </div>
                        </div>
                      </div>
                    </td>
                    {METRICS.map(m => (
                      <React.Fragment key={m.key}>
                        <td style={{ ...cellStyle, borderLeft: groupBorder, fontWeight: 600, color: 'var(--text-heading)' }}>
                          {renderValue(row, m.key, 'target_period')}
                        </td>
                        <td style={{ ...cellStyle, color: 'var(--text-muted)' }}>{renderValue(row, m.key, 'base_period')}</td>
                        <td style={cellStyle}>{renderVariation(row, m.key)}</td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                  {publishedOnly && publishedCount === 0
                    ? "Aucune société n'a encore publié cette période."
                    : 'Aucune société ne correspond à votre recherche.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
